/**
 * AdminFieldDefEditModal
 *
 * Create / edit / delete a custom product field definition. Covers the full
 * definition: key, label, data type, scope + "apply to" (one / all / select
 * retailers), URL template (url-type only), and output configuration for the
 * BOM / Placement Guide columns.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Save, Loader2, Trash2, AlertTriangle, Link2 } from 'lucide-react';
import {
  AdminFieldDef,
  FieldDefPayload,
  FieldDefRetailerSummary,
  createFieldDef,
  fetchFieldDef,
  patchFieldDef,
} from '../../services/adminFieldDefsApi';
import { CustomFieldType } from '../../types';

interface Props {
  fieldDefId: number | null;
  isCreating: boolean;
  retailers: FieldDefRetailerSummary[];
  /** All defs — used to suggest {placeholder} keys for URL templates. */
  allDefs: AdminFieldDef[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: (id: number, confirmKey: string) => void;
}

interface DraftState {
  fieldKey: string;
  label: string;
  dataType: CustomFieldType;
  scope: 'trusscore' | 'retailer';
  applyMode: 'all' | 'select';
  retailerIds: number[];
  urlTemplate: string;
  showInBom: boolean;
  showInPlacementGuide: boolean;
  columnHeading: string;
  outputMode: 'replace' | 'alongside' | 'standalone';
  columnOrder: number;
  productNumberHeading: string;
  isActive: boolean;
  sortOrder: number;
}

const blankDraft = (): DraftState => ({
  fieldKey: '',
  label: '',
  dataType: 'varchar',
  scope: 'retailer',
  applyMode: 'select',
  retailerIds: [],
  urlTemplate: '',
  showInBom: false,
  showInPlacementGuide: false,
  columnHeading: '',
  outputMode: 'standalone',
  columnOrder: 0,
  productNumberHeading: '',
  isActive: true,
  sortOrder: 0,
});

const fromDef = (d: AdminFieldDef): DraftState => ({
  fieldKey: d.fieldKey,
  label: d.label,
  dataType: d.dataType,
  scope: d.scope,
  applyMode: d.allRetailers ? 'all' : 'select',
  retailerIds: d.retailerIds,
  urlTemplate: d.urlTemplate ?? '',
  showInBom: d.showInBom,
  showInPlacementGuide: d.showInPlacementGuide,
  columnHeading: d.columnHeading ?? '',
  outputMode: d.outputMode,
  columnOrder: d.columnOrder,
  productNumberHeading: d.productNumberHeading ?? '',
  isActive: d.isActive,
  sortOrder: d.sortOrder,
});

export const AdminFieldDefEditModal: React.FC<Props> = ({
  fieldDefId, isCreating, retailers, allDefs, onClose, onSaved, onDeleted,
}) => {
  const [draft, setDraft] = useState<DraftState>(blankDraft());
  const [loading, setLoading] = useState(!isCreating);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valueCount, setValueCount] = useState(0);
  const [deleteKeyInput, setDeleteKeyInput] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    if (isCreating || fieldDefId === null) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchFieldDef(fieldDefId);
        if (cancelled) return;
        setDraft(fromDef(data.fieldDef));
        setValueCount(data.valueCount);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load field');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fieldDefId, isCreating]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const set = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft(prev => ({ ...prev, [key]: value }));

  // Placeholder keys available for URL templates: this field's own ({self})
  // plus sibling keys in the same scope/retailer set.
  const placeholderKeys = useMemo(() => {
    const keys = new Set<string>(['self']);
    if (draft.fieldKey) keys.add(draft.fieldKey);
    allDefs.forEach(d => {
      if (d.id === fieldDefId) return;
      if (d.scope !== draft.scope) return;
      keys.add(d.fieldKey);
    });
    return Array.from(keys);
  }, [allDefs, draft.scope, draft.fieldKey, fieldDefId]);

  const buildPayload = (): FieldDefPayload => {
    const payload: FieldDefPayload = {
      fieldKey: draft.fieldKey.trim(),
      label: draft.label.trim(),
      dataType: draft.dataType,
      scope: draft.scope,
      showInBom: draft.showInBom,
      showInPlacementGuide: draft.showInPlacementGuide,
      columnHeading: draft.columnHeading.trim() || null,
      outputMode: draft.outputMode,
      columnOrder: draft.columnOrder,
      productNumberHeading: draft.productNumberHeading.trim() || null,
      isActive: draft.isActive,
      sortOrder: draft.sortOrder,
      // Link template is a universal output option, independent of data type.
      urlTemplate: draft.urlTemplate.trim() || null,
    };
    if (draft.scope === 'retailer') {
      payload.applyMode = draft.applyMode;
      if (draft.applyMode === 'select') payload.retailerIds = draft.retailerIds;
    }
    return payload;
  };

  const handleSave = async () => {
    if (!draft.fieldKey.trim() || !draft.label.trim()) {
      setError('Key and label are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isCreating) {
        await createFieldDef(buildPayload());
      } else if (fieldDefId !== null) {
        await patchFieldDef(fieldDefId, buildPayload());
      }
      onSaved();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const keyMatchesForDelete = !isCreating && deleteKeyInput.trim() === draft.fieldKey;

  const toggleRetailer = (id: number) => {
    set('retailerIds', draft.retailerIds.includes(id)
      ? draft.retailerIds.filter(r => r !== id)
      : [...draft.retailerIds, id]);
  };

  return (
    <div
      className={`modal d-block fade ${visible ? 'show' : ''}`}
      tabIndex={-1}
      style={{ transition: 'opacity 0.25s ease', backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">
              {isCreating ? 'Add Product Field' : `Edit Field — ${draft.label || draft.fieldKey}`}
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5 text-muted">
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
              </div>
            ) : (
              <>
                {error && (
                  <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}

                {/* Core */}
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Label</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={draft.label}
                      onChange={e => set('label', e.target.value)}
                      placeholder="e.g. In Store SKU"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Field Key</label>
                    <input
                      type="text"
                      className="form-control form-control-sm font-monospace"
                      value={draft.fieldKey}
                      onChange={e => set('fieldKey', e.target.value)}
                      placeholder="e.g. inStoreSku"
                    />
                    <div className="form-text" style={{ fontSize: '0.7em' }}>
                      Letters, digits, underscore. Used in URL-template placeholders.
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Data Type</label>
                    <select
                      className="form-select form-select-sm"
                      value={draft.dataType}
                      onChange={e => set('dataType', e.target.value as CustomFieldType)}
                    >
                      <option value="varchar">Text (varchar)</option>
                      <option value="numeric">Numeric</option>
                      <option value="url">URL (value is a web address)</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Scope</label>
                    <select
                      className="form-select form-select-sm"
                      value={draft.scope}
                      onChange={e => set('scope', e.target.value as 'trusscore' | 'retailer')}
                    >
                      <option value="retailer">Retailer-specific</option>
                      <option value="trusscore">Trusscore default (global)</option>
                    </select>
                  </div>
                </div>

                {/* Apply-to (retailer scope only) */}
                {draft.scope === 'retailer' && (
                  <div className="border rounded-3 p-3 mb-3 bg-light">
                    <label className="form-label small fw-semibold mb-2">Apply to</label>
                    <div className="d-flex gap-3 mb-2">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="apply-all"
                          checked={draft.applyMode === 'all'}
                          onChange={() => set('applyMode', 'all')}
                        />
                        <label className="form-check-label small" htmlFor="apply-all">All retailers (incl. future)</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="apply-select"
                          checked={draft.applyMode === 'select'}
                          onChange={() => set('applyMode', 'select')}
                        />
                        <label className="form-check-label small" htmlFor="apply-select">Select retailers</label>
                      </div>
                    </div>
                    {draft.applyMode === 'select' && (
                      <div className="d-flex flex-wrap gap-2">
                        {retailers.map(r => (
                          <div key={r.id} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`ret-${r.id}`}
                              checked={draft.retailerIds.includes(r.id)}
                              onChange={() => toggleRetailer(r.id)}
                            />
                            <label className="form-check-label small" htmlFor={`ret-${r.id}`}>
                              {r.name}{r.isPartner && <span className="badge text-bg-info ms-1" style={{ fontSize: '0.6rem' }}>partner</span>}
                            </label>
                          </div>
                        ))}
                        {retailers.length === 0 && <span className="text-muted small">No retailers available.</span>}
                      </div>
                    )}
                  </div>
                )}

                {/* Output config — the toggles mark the field as an output column
                    (shown by default). Order, heading, alignment, and per-output
                    visibility are managed in the Column Layout Designer. */}
                <div className="border rounded-3 p-3 mb-3">
                  <label className="form-label small fw-semibold mb-2">Estimate output</label>
                  <div className="d-flex flex-wrap gap-3 mb-2">
                    <div className="form-check form-switch">
                      <input type="checkbox" className="form-check-input" role="switch" id="show-bom"
                        checked={draft.showInBom} onChange={e => set('showInBom', e.target.checked)} />
                      <label className="form-check-label small" htmlFor="show-bom">Show in Bill of Materials</label>
                    </div>
                    <div className="form-check form-switch">
                      <input type="checkbox" className="form-check-input" role="switch" id="show-pg"
                        checked={draft.showInPlacementGuide} onChange={e => set('showInPlacementGuide', e.target.checked)} />
                      <label className="form-check-label small" htmlFor="show-pg">Show in Placement Guide</label>
                    </div>
                  </div>
                  <div className="form-text mb-3" style={{ fontSize: '0.72em' }}>
                    These toggles make the field available as a column (shown by default). Column order,
                    heading, alignment, and per-output (web / print / PDF) visibility are set in the{' '}
                    <strong>Column Layout Designer</strong>.
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label small fw-semibold d-flex align-items-center gap-1">
                        <Link2 size={13} /> Link template <span className="text-muted fw-normal">(optional — works with any field type)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        value={draft.urlTemplate}
                        onChange={e => set('urlTemplate', e.target.value)}
                        placeholder="https://www.homedepot.ca/product/trusscore/{self}"
                      />
                      <div className="form-text" style={{ fontSize: '0.7em' }}>
                        When set, the value renders as a clickable link (opens in a new tab). Must start with <code>https://</code>.
                        Placeholders: {placeholderKeys.map(k => (
                          <code key={k} className="me-1">{`{${k}}`}</code>
                        ))}
                        — <code>{'{self}'}</code> is this field&apos;s own value. Leave blank for a plain value
                        (or use the <strong>URL</strong> data type when the value is itself a full web address).
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <div className="form-check form-switch">
                    <input type="checkbox" className="form-check-input" role="switch" id="fd-active"
                      checked={draft.isActive} onChange={e => set('isActive', e.target.checked)} />
                    <label className="form-check-label small" htmlFor="fd-active">Active</label>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label small fw-semibold mb-0">Sort order</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: 90 }}
                      value={draft.sortOrder}
                      onChange={e => set('sortOrder', parseInt(e.target.value, 10) || 0)}
                    />
                  </div>
                </div>

                {/* Delete (edit only) */}
                {!isCreating && (
                  <div className="border border-danger-subtle rounded-3 p-3 mt-4">
                    <h6 className="text-danger fw-bold small d-flex align-items-center gap-1 mb-2">
                      <Trash2 size={14} /> Delete field
                    </h6>
                    <p className="text-muted small mb-2">
                      Removes the definition and {valueCount} stored value{valueCount === 1 ? '' : 's'} across all
                      products. Type the field key <code>{draft.fieldKey}</code> to confirm.
                    </p>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        style={{ maxWidth: 220 }}
                        value={deleteKeyInput}
                        onChange={e => setDeleteKeyInput(e.target.value)}
                        placeholder={draft.fieldKey}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                        disabled={!keyMatchesForDelete || saving}
                        onClick={() => fieldDefId !== null && onDeleted(fieldDefId, deleteKeyInput.trim())}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer bg-light">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving
                ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                : <><Save size={16} /> {isCreating ? 'Create Field' : 'Save Changes'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFieldDefEditModal;
